import {BaseElement} from "../../../interfaces/BaseElement";
import {EventBus} from "../../../interfaces/EventBus";
import {PopUpPage} from "../../../interfaces/pages/PopUp";
import {resetWebFilters} from "../search";
import {changeLangToCurrent} from "../../../components/langSwitcher/langSwitcher";
import {ActiveFiltersCounter} from "./ActiveFiltersCounter";

export class ShrinkSearch {
    #elementWithFilters;
    #filterCloneElement;
    #elementWithFiltersParent;
    #filterPopUp;
    #contentWrapper;
    #filtersWrapper;
    #filterCounter;
    #moreButton;
    #mainClearAll;
    #buttonsWrapper;
    #AMOUNT_OF_VISIBLE_FILTERS = 3;
    #multipleSearchContainer = document.querySelector('.multiple__search__container');
    #isArrowCollapse = config.features?.search?.filterType === "arrowCollapse";
    #arrowButton;
    #arrowButtonText;
    #filtersExpanded = false;

    constructor(elementWithFilters) {
        this.#elementWithFilters = elementWithFilters;
        this.#elementWithFiltersParent = new BaseElement(elementWithFilters.parentElement);

        if (this.#isArrowCollapse) {
            this.#addArrowButton();
        } else {
            this.#filterPopUp = new PopUpPage('');
            this.#filterPopUp.root.addEventListener('click', (event) => {
                if (!event.target.classList.contains('pop-up-page__content-wrapper'))
                    return;

                PopUpPage.animation('hide');
                this.#putMainElementBack();
            });
            this.#filterPopUp.closeButton.event('click', this.#putMainElementBack.bind(this));
            this.#filterPopUp.addClass('filter-pop-up-page');
            this.#filterPopUp.addClass('hidden');
        }
        
        this.#addMoreButton();
        this.#composeContentElement();
        this.#hideFilterChildrenElementsExceedsLimit();
        
        if (!this.#isArrowCollapse) {
            this.#filterCounter.update();
            EventBus.add('shrinkSearchPutMainElementBack', this.#putMainElementBack.bind(this));
        }
            
    }

    #hideFilterChildrenElementsExceedsLimit(withTransition = false) {
        if (withTransition) {
            [...this.#elementWithFilters.children].forEach((child, index) => {
                if (index >= this.#AMOUNT_OF_VISIBLE_FILTERS) {
                    child.classList.add('opaque');
                    child.addEventListener('transitionend', () => {
                        child.classList.add('hidden');
                    }, { once: true });
                }
            });
        } else {
            [...this.#elementWithFilters.children].forEach((child, index) => {
                if (index >= this.#AMOUNT_OF_VISIBLE_FILTERS) {
                    child.classList.add('hidden');
                    if (this.#isArrowCollapse)
                        child.classList.add('opaque');
                }
            });
        }
    }

    #showAllFilterChildrenElements() {
        [...this.#elementWithFilters.children].forEach((child) => {
            child.classList.remove('hidden');
        });
    }

    #addMoreButton() {
        this.#moreButton = new BaseElement('button')
            .addClass('search__more-button', 'language-string')
            .innerHtml(changeLangToCurrent('All filters'))
            .addAttribute('data-dictionary', 'All filters');
            
        if (!this.#isArrowCollapse) {
            this.#moreButton.event('click', () => {
                this.#swapMainWithCloneElements();
                this.#showAllFilterChildrenElements();
                this.#filterPopUp.removeClass('hidden');
                PopUpPage.animation('show');
            });
        } else {
            this.#moreButton.addClass('search__more-button--arrow-collapse');
        }
    }

    #addArrowButton() {
        this.#arrowButton = new BaseElement('button')
            .addClass('search__arrow-button')
            .addAttribute('aria-label', 'Toggle filters');
        new BaseElement('span')
            .addClass('search__arrow-button-icon', 'icon-down2')
            .appendTo(this.#arrowButton);

        this.#arrowButtonText = new BaseElement('span')
            .addClass('search__arrow-button-text')
            .innerHtml(changeLangToCurrent('All filters'));
    }

    #toggleFilters() {
        this.#filtersExpanded = !this.#filtersExpanded;
        
        if (this.#filtersExpanded) {
            this.#showAllFilterChildrenElements();
            this.#arrowButton.DOMElement.classList.add('expanded');
            this.#elementWithFilters.classList.add('filters-expanded');
            setTimeout(() => {
                [...this.#elementWithFilters.children].forEach((child) => {
                    child.classList.remove('opaque');
                });
            }, 100);
        } else {
            this.#hideFilterChildrenElementsExceedsLimit(true);
            this.#arrowButton.DOMElement.classList.remove('expanded');
            this.#elementWithFilters.classList.remove('filters-expanded');
        }

        if (!this.#isArrowCollapse)
        this.#filterCounter.update();
    }

    #composeContentElement() {
        const mainWrapper = new BaseElement()
            .addClass('search__main-buttons-wrapper')
            .appendTo(this.#elementWithFiltersParent)
            .DOMElement;

        if (this.#isArrowCollapse) {
            mainWrapper.addEventListener('click', this.#toggleFilters.bind(this));
            mainWrapper.classList.add('search__main-buttons-wrapper--arrow-collapse');
        }
        
        const moreWrapper = !this.#isArrowCollapse && new BaseElement()
            .addClass('search__more-wrapper')
            .appendTo(mainWrapper);

        if (this.#isArrowCollapse) {
            this.#moreButton.appendTo(mainWrapper);
            this.#arrowButton.appendTo(mainWrapper);
        } else {
            this.#moreButton.appendTo(moreWrapper);

            this.#filterCounter = new ActiveFiltersCounter();
            this.#filterCounter.rootElement.appendTo(moreWrapper);
            this.#resetButton(mainWrapper);
        }
        
        // Only create popup content if not using arrow collapse
        if (!this.#isArrowCollapse) {
            this.#contentWrapper = new BaseElement()
                .addClass('filter-pop-up__content-wrapper');

            this.#filtersWrapper = new BaseElement()
                .addClass('filter-pop-up__filters-wrapper')
                .appendTo(this.#contentWrapper);

            this.#createPopUpButtons();

            this.#filterPopUp.contentElement.appendChild(this.#contentWrapper.DOMElement);
        }

        if (this.#multipleSearchContainer)
            this.#elementWithFilters.appendChild(this.#multipleSearchContainer);
    }

    #createPopUpButtons() {
        this.#buttonsWrapper = new BaseElement()
            .addClass('filter-pop-up__buttons')
            .appendTo(this.#contentWrapper);

        this.#applyButton();
        this.#resetButton();
    }

    #applyButton() {
        new BaseElement('button')
            .addClass('filter-pop-up__button', 'filter-pop-up__apply', 'button', 'language-string')
            .event('click', () => {
                PopUpPage.animation('hide');
                this.#putMainElementBack();
            })
            .addAttribute('data-dictionary', 'Apply filters')
            .innerHtml(changeLangToCurrent('Apply filters'))
            .appendTo(this.#buttonsWrapper);
    }

    #resetButton(parent = this.#buttonsWrapper ) {
        return new BaseElement('button')
            .addClass('filter-pop-up__button', 'mobile-search__clear-filter', 'language-string', 'search__main-clear')
            .event('click', () => {
                resetWebFilters();
                this.#filterCounter.update();
            })
            .addAttribute('data-dictionary', 'Clear all')
            .innerHtml(changeLangToCurrent('Clear all'))
            .appendTo(parent);
    }

    #swapMainWithCloneElements() {
        // Only for popup mode, not for arrow collapse
        if (this.#isArrowCollapse) return;
        
        this.#filterCloneElement = this.#elementWithFilters.cloneNode(true);
        this.#filterCloneElement.classList.add('filter-clone');
        [...this.#filterCloneElement.querySelectorAll('[id]')]
            .forEach(elementWithId => { elementWithId.removeAttribute('id'); });

        this.#filtersWrapper.appendElement(this.#elementWithFilters);
        this.#elementWithFiltersParent.appendElement(this.#filterCloneElement);
    }

    #putMainElementBack() {
        // Only for popup mode, not for arrow collapse
        if (this.#isArrowCollapse) return;
        
        if (this.#filterCloneElement)
            this.#filterCloneElement.remove();
        this.#elementWithFiltersParent.appendElement(this.#elementWithFilters);
        this.#hideFilterChildrenElementsExceedsLimit();
        this.#filterCounter.update();
    }
}
